import React from 'react';

import FolderLayout from 'Components/HumanResources/StaffFolder/FolderLayout';
import {Card, SmallLink, Details, SimpleTable, PageBar, formatDate, joinNames, plural} from 'Components/HumanResources/StaffFolder/Ui';

import DocumentsApi from 'Services/Api/Admin/HumanResources/Documents';

/**
 * Staff folder: HR details. Read-only, with Edit links to the existing edit pages.
 * Payment details stay hidden until "Show" is pressed, so they aren't on screen on a shared computer.
 */
export default class HrDetails extends React.Component {
    /**
     * @var state
     */
    state = {
        showPayment: false,
        documents: null,
        documentsMeta: null,
        documentsPage: 1,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount = () => {
        this.loadDocuments();
    };

    /**
     * @method loadDocuments
     */
    loadDocuments = async () => {
        const response = await DocumentsApi.get({user: this.props.match.params.user}, {
            page: this.state.documentsPage,
        });

        this.setState({
            documents: response.success ? response.data.data : [],
            documentsMeta: response.success ? response.data.meta : null,
        });
    };

    /**
     * @method download
     * @param {object} document
     */
    download = (document) => {
        DocumentsApi.downloadFile(
            `/admin/human-resources/${this.props.match.params.user}/documents/${document.id}/download`,
            {},
            `${document.name || 'document-' + document.id}.pdf`
        );
    };

    /**
     * @method masked
     * @param {string|null} value
     * @param {boolean} lastTwo Show the last two characters, e.g. "Ends 21".
     * @return {string|null}
     */
    masked = (value, lastTwo = false) => {
        if (!value) {
            return null;
        }

        if (this.state.showPayment) {
            return value;
        }

        return lastTwo ? 'Ends ' + String(value).slice(-2) : 'Hidden';
    };

    /**
     * @method address
     * @param {object|null} address
     * @return {string|null}
     */
    address = (address) => {
        if (!address) {
            return null;
        }

        return [address.address_1, address.address_2, address.city, address.county, address.post_code]
            .filter(Boolean)
            .join(', ') || null;
    };

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const userId = this.props.match.params.user;
        const {showPayment, documents, documentsMeta, documentsPage} = this.state;
        const editProfile = <SmallLink to={`/admin/human-resources/profile/${userId}`}>Edit</SmallLink>;

        return (
            <FolderLayout userId={userId}>
                {({user, overview}) => {
                    const hr = overview.hr;
                    const address = user.address ?? {};
                    const emergency = [user.emergency_contact_first_name, user.emergency_contact_last_name].filter(Boolean).join(' ');

                    return (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                            <Card title="Account details" id="d-acc" action={<SmallLink to={`/admin/users/store/${userId}`}>Edit</SmallLink>}>
                                <Details items={[
                                    ['First name', user.first_name],
                                    ['Last name', user.last_name],
                                    ['Email', user.email],
                                    ['Job title', user.job_title],
                                ]}/>
                            </Card>

                            <Card title="Employment" id="d-emp" action={<SmallLink to={`/admin/human-resources/settings/${userId}`}>Edit</SmallLink>}>
                                <Details items={[
                                    ['Start date', hr.start_date ? formatDate(hr.start_date) : null],
                                    ['Services', hr.services.length ? joinNames(hr.services) : null],
                                    ['Days per week', hr.days_per_week],
                                    ['Hours per day', hr.hours_per_day],
                                ]}/>
                            </Card>

                            <Card title="Personal details" id="d-per" action={editProfile}>
                                <Details items={[
                                    ['Address 1', address.address_1],
                                    ['Address 2', address.address_2],
                                    ['City', address.city],
                                    ['County', address.county],
                                    ['Post code', address.post_code],
                                    ['Contact number', user.contact_number],
                                    ['Personal email', user.personal_email],
                                    ['Date of birth', user.date_of_birth ? formatDate(user.date_of_birth) : null],
                                ]}/>
                            </Card>

                            <Card
                                title="Payment details"
                                id="d-pay"
                                action={
                                    <div className="flex items-center gap-4">
                                        <button
                                            type="button"
                                            className="font-bold text-app-leading hover:underline"
                                            aria-pressed={showPayment}
                                            onClick={() => this.setState({showPayment: !showPayment})}
                                        >
                                            {showPayment ? 'Hide' : 'Show'}
                                        </button>
                                        {editProfile}
                                    </div>
                                }
                            >
                                <Details items={[
                                    ['Bank name', user.bank_name ? (showPayment ? user.bank_name : 'On file') : null],
                                    ['Name on account', user.bank_account_name ? (showPayment ? user.bank_account_name : 'On file') : null],
                                    ['Account number', this.masked(user.bank_account_number, true)],
                                    ['Sort code', this.masked(user.bank_sort_code)],
                                    ['NI number', this.masked(user.national_insurance)],
                                ]}/>
                                <p className="mt-3 text-sm text-gray-600">
                                    Hidden until you press Show, so they are not on screen when the folder is open on a shared computer.
                                </p>
                            </Card>

                            <Card title="Emergency contact" id="d-emg" action={editProfile}>
                                <Details items={[
                                    ['Name', emergency || null],
                                    ['Relationship', user.emergency_contact_relationship],
                                    ['Contact number', user.emergency_contact_number],
                                    ['Contact email', user.emergency_contact_email],
                                    ['Address', this.address(user.emergency_contact_address)],
                                ]}/>
                            </Card>

                            <Card
                                title="Documents"
                                id="d-doc"
                                subtitle={documentsMeta ? plural(documentsMeta.total, 'document') : null}
                                action={<SmallLink to={`/admin/human-resources/documents/${userId}`}>Upload or manage</SmallLink>}
                            >
                                <SimpleTable
                                    headings={['Document', 'Added', '']}
                                    perPage={0}
                                    empty={documents ? 'No documents yet.' : 'Loading...'}
                                    rows={(documents ?? []).map(document => [
                                        <span className="font-bold">{document.name}</span>,
                                        formatDate(document.created_at),
                                        <button type="button" className="font-bold text-app-leading hover:underline" onClick={() => this.download(document)}>
                                            Download
                                        </button>,
                                    ])}
                                />

                                {documentsMeta && (
                                    <PageBar
                                        page={documentsPage}
                                        perPage={documentsMeta.per_page}
                                        total={documentsMeta.total}
                                        goToPage={(next) => this.setState({documentsPage: next}, this.loadDocuments)}
                                    />
                                )}
                            </Card>
                        </div>
                    );
                }}
            </FolderLayout>
        );
    }
}
